/**
 * Adding and editing a vault entry.
 *
 * ## The password field on an EDIT is empty and does not mean "empty"
 *
 * This is the one thing the form has to get right. The stored password is never
 * loaded into this dialog - the endpoints that feed it do not return one - so an
 * edit opens with a blank field. Blank therefore means "leave it alone", and the
 * field says so in its placeholder. Clearing a stored password is a separate,
 * explicit press, because the alternative is a rename that silently wipes the
 * credential.
 *
 * The same argument applies to the notes, which are sealed for the same reason:
 * the Perfex material arriving in them IS pasted credentials.
 */
import { useState, type ReactNode } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import {
  VAULT_KINDS,
  createVaultEntry,
  updateVaultEntry,
  type VaultEntry,
  type VaultInput,
  type VaultKind,
} from "../../../lib/vault";
import { Btn, CheckField, MenuField, Modal, useToast } from "../../ui";
import { ClientPicker, ProjectPicker } from "../sales/shared";
import { kindLabelKey } from "./kinds";

/** A label over a field, the way the design writes forms. */
function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <>
      <label className="crm-lab">{label}</label>
      {children}
      {hint ? <p className="crm-mut mb-2 mt-1 text-[12px]">{hint}</p> : null}
    </>
  );
}

/**
 * One sunken pill holding a plain input.
 *
 * ## Nothing in this form may talk to a password manager
 *
 * `autoComplete="off"` alone does NOT do it, and this dialog is where that shows.
 * Chrome sees a form with a password field, decides the form is a login, fills
 * the operator's own saved CRM account into the nearest text field above the
 * password, and paints both fields in its own blue. What the operator saw was
 * their own login sitting in a customer's address field, on top of the value
 * they were editing.
 *
 * Three things are needed, and none of them works on its own:
 *  - `new-password` on the password field. It is the one value Chrome honours as
 *    "this is not the login you saved", and it is what also takes the fill off
 *    the text field above it.
 *  - a `name` that means nothing to a heuristic. Chrome classifies fields by
 *    their name and their label, and "Aadress" is a street address to it. Hence
 *    `vault-link` rather than `url`, `vault-acct` rather than `username`.
 *  - the opt-out attributes the third-party managers read: `data-1p-ignore`
 *    (1Password), `data-lpignore` (LastPass), `data-form-type` (Dashlane).
 *
 * The blue is taken off separately in `index.css`, because a browser that fills
 * anyway must not also break the design. And the reason for all of it is not
 * cosmetic: a browser that offers to remember a customer's server login has just
 * made a second, unmanaged copy of it outside the vault.
 */
function Pill({
  name,
  value,
  onChange,
  placeholder,
  type,
  inputMode,
  required,
  autoComplete,
}: {
  /** Deliberately not the plain word for what the field holds. See above. */
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: "numeric" | "text";
  required?: boolean;
  /** Only the password overrides this, and only to `new-password`. */
  autoComplete?: string;
}) {
  return (
    <label className="crm-inp">
      <input
        name={name}
        type={type ?? "text"}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete ?? "off"}
        data-1p-ignore=""
        data-lpignore="true"
        data-form-type="other"
        spellCheck={false}
      />
    </label>
  );
}

/**
 * What a screen that already knows the answer can fill in for a NEW entry.
 *
 * Only ever read when `entry` is null. An edit opens on the stored record, and
 * a preset that could win over it would be a dialog that quietly rewrote the
 * row the operator opened to correct.
 *
 * The point is not saving keystrokes. A credential added from the project page
 * and saved without `projectId` disappears from the list it was created in, and
 * the operator's only evidence is a list that did not change - which reads as a
 * save that failed rather than as a row filed somewhere else.
 */
export interface VaultPreset {
  title?: string;
  kind?: VaultKind;
  clientId?: string | null;
  projectId?: string | null;
  url?: string | null;
}

export function VaultModal({
  entry,
  preset,
  onSaved,
  onClose,
}: {
  /** The entry being edited, or null for a new one. */
  entry: VaultEntry | null;
  /** Starting values for a new entry. Ignored while editing - see above. */
  preset?: VaultPreset;
  onSaved: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();

  // The preset is folded in ONLY where the entry has nothing to say, which on an
  // edit is nowhere: `entry` is checked first in every one of these, so an open
  // record always wins. Read once, as the initial state - a preset that kept
  // reasserting itself would fight the operator's typing.
  const [title, setTitle] = useState(entry?.title ?? preset?.title ?? "");
  const [kind, setKind] = useState<VaultKind>((entry?.kind as VaultKind) ?? preset?.kind ?? "other");
  const [clientId, setClientId] = useState(entry?.clientId ?? preset?.clientId ?? "");
  const [projectId, setProjectId] = useState(entry?.projectId ?? preset?.projectId ?? "");
  const [host, setHost] = useState(entry?.host ?? "");
  const [port, setPort] = useState(entry?.port ? String(entry.port) : "");
  const [username, setUsername] = useState(entry?.username ?? "");
  const [url, setUrl] = useState(entry?.url ?? preset?.url ?? "");
  const [secret, setSecret] = useState("");
  const [notes, setNotes] = useState("");
  // Explicit erasure of a stored value, kept apart from "the field is blank".
  const [clearSecret, setClearSecret] = useState(false);
  const [clearNotes, setClearNotes] = useState(false);
  const [busy, setBusy] = useState(false);

  const editing = !!entry;

  /**
   * What one sealed field contributes to the request body.
   *
   * `undefined` drops the key from the JSON entirely, which is what tells the
   * server to keep what it has. Returning `null` is the explicit clear. See
   * `sealPatch` on the server for the other half of this contract.
   */
  function sealedField(typed: string, clear: boolean): string | null | undefined {
    if (clear) return null;
    if (typed === "") return undefined;
    return typed;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      const body: VaultInput = {
        title: title.trim(),
        kind,
        clientId: clientId || null,
        projectId: projectId || null,
        host: host.trim() || null,
        port: port.trim() ? Number(port) : null,
        username: username.trim() || null,
        url: url.trim() || null,
        secret: sealedField(secret, clearSecret),
        notes: sealedField(notes, clearNotes),
      };
      if (entry) await updateVaultEntry(entry.id, body);
      else await createVaultEntry(body);
      onSaved();
      onClose();
    } catch (err) {
      // The one refusal with a name: the server has no usable key, so it will
      // not write ciphertext and must not write plaintext. Saying that plainly
      // beats "something went wrong" on a form the operator just typed a
      // password into.
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      toast(t(code === "vault_key_missing" ? "vault.keyMissingShort" : "common.error"));
    } finally {
      setBusy(false);
    }
  }

  const heading = editing ? t("vault.editTitle") : t("vault.addTitle");

  return (
    <Modal title={heading} label={heading} onClose={onClose}>
      <form onSubmit={submit} autoComplete="off">
        <Field label={t("vault.name")}>
          <Pill
            name="vault-label"
            value={title}
            onChange={setTitle}
            placeholder={t("vault.namePlaceholder")}
            required
          />
        </Field>

        <Field label={t("vault.kind")}>
          <MenuField
            value={kind}
            onChange={(v) => setKind(v as VaultKind)}
            label={t("vault.kind")}
            options={VAULT_KINDS.map((k) => ({ value: k, label: t(kindLabelKey(k)) }))}
          />
        </Field>

        <Field label={t("vault.client")}>
          <ClientPicker
            value={clientId}
            onChange={(next) => {
              // A project belongs to one client, so changing the client
              // invalidates the project chosen under the old one.
              if (next !== clientId) setProjectId("");
              setClientId(next);
            }}
          />
        </Field>

        <Field label={t("vault.project")}>
          <ProjectPicker clientId={clientId} value={projectId} onChange={setProjectId} />
        </Field>

        <div className="grid grid-cols-[1fr_120px] gap-3">
          <div>
            <Field label={t("vault.host")}>
              <Pill name="vault-server" value={host} onChange={setHost} placeholder={t("common.example.ftpHost")} />
            </Field>
          </div>
          <div>
            <Field label={t("vault.port")}>
              <Pill name="vault-portnum" value={port} onChange={setPort} inputMode="numeric" placeholder="21" />
            </Field>
          </div>
        </div>

        <Field label={t("vault.username")}>
          <Pill name="vault-acct" value={username} onChange={setUsername} />
        </Field>

        <Field label={t("vault.url")}>
          <Pill name="vault-link" value={url} onChange={setUrl} placeholder="https://" />
        </Field>

        <Field
          label={t("vault.secret")}
          hint={editing && entry?.hasSecret ? t("vault.secretKeepHint") : undefined}
        >
          <Pill
            name="vault-seal"
            value={secret}
            onChange={(v) => {
              setSecret(v);
              // Typing a new value and asking for a clear are contradictory, so
              // the typing wins and the checkbox lets go.
              if (v) setClearSecret(false);
            }}
            type="password"
            // The one field that says something other than "off": see Pill.
            autoComplete="new-password"
            placeholder={editing && entry?.hasSecret ? t("vault.secretUnchanged") : ""}
          />
        </Field>

        {editing && entry?.hasSecret ? (
          <CheckField
            className="mt-1"
            checked={clearSecret}
            onChange={(next) => {
              setClearSecret(next);
              if (next) setSecret("");
            }}
            label={
              <span className="flex items-center gap-2">
                <Trash2 className="size-4" />
                {t("vault.clearSecret")}
              </span>
            }
          />
        ) : null}

        <Field
          label={t("vault.notes")}
          hint={editing && entry?.hasNotes ? t("vault.notesKeepHint") : t("vault.notesHint")}
        >
          <textarea
            className="crm-area"
            name="vault-memo"
            autoComplete="off"
            data-1p-ignore=""
            data-lpignore="true"
            data-form-type="other"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              if (e.target.value) setClearNotes(false);
            }}
            placeholder={editing && entry?.hasNotes ? t("vault.secretUnchanged") : ""}
            spellCheck={false}
          />
        </Field>

        {editing && entry?.hasNotes ? (
          <CheckField
            className="mt-1"
            checked={clearNotes}
            onChange={(next) => {
              setClearNotes(next);
              if (next) setNotes("");
            }}
            label={
              <span className="flex items-center gap-2">
                <Trash2 className="size-4" />
                {t("vault.clearNotes")}
              </span>
            }
          />
        ) : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !title.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
